const STORAGE_KEY = 'gh.language';
const DEFAULT_LANGUAGE = 'en';
const SUPPORTED_LANGUAGES = ['sv', 'dk', 'no', 'en', 'de', 'nl'];
const COUNTRIES = { sv: 'SE', dk: 'DK', no: 'NO', en: 'EN', de: 'DE', nl: 'NL' };

let currentLanguage = null;
let currentPathPrefix = null;

const isSupportedLanguage = (l) => SUPPORTED_LANGUAGES.includes(l);

const getLanguageFromPath = (pathname) => {
  const [, firstSegment] = pathname.split('/');
  return isSupportedLanguage(firstSegment) ? firstSegment : null;
};

const detectFallbackLanguage = () => {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && isSupportedLanguage(stored)) return stored;

  const browserLang = navigator.languages?.[0]?.slice(0, 2) || navigator.language?.slice(0, 2);

  return isSupportedLanguage(browserLang) ? browserLang : DEFAULT_LANGUAGE;
};

/**
 * Called at app start and on navigation.
 * Updates only if the language prefix in the URL has actually changed.
 */
export const syncLanguageWithPath = () => {
  const pathLang = getLanguageFromPath(window.location.pathname);
  const newPrefix = pathLang; // null if no prefix

  // If prefix unchanged, return current language
  if (currentLanguage && newPrefix === currentPathPrefix) {
    return currentLanguage;
  }

  // If prefix found, use it
  if (pathLang) {
    currentLanguage = pathLang;
    currentPathPrefix = newPrefix;
    window.localStorage.setItem(STORAGE_KEY, currentLanguage);
    return currentLanguage;
  }

  // No prefix: use fallback
  currentLanguage = detectFallbackLanguage();
  currentPathPrefix = null;
  window.localStorage.setItem(STORAGE_KEY, currentLanguage);
  return currentLanguage;
};

export const getCurrentLanguage = () => {
  if (!currentLanguage) return syncLanguageWithPath();
  return currentLanguage;
};

// Old methods below for backward compatibility
// Check what is still used and remove later
const getLanguage = () => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && isSupportedLanguage(stored)) {
    return stored;
  }

  const detected = detectLanguage();
  localStorage.setItem(STORAGE_KEY, detected);
  return detected;
};

export default getLanguage;

export const detectLanguage = () => {
  const pathLang = getLanguageFromPath(window.location.pathname);
  if (pathLang) {
    localStorage.setItem(STORAGE_KEY, pathLang);
    return pathLang;
  }

  const browserLang = navigator.languages?.[0]?.slice(0, 2) || navigator.language?.slice(0, 2);
  const language = isSupportedLanguage(browserLang) ? browserLang : DEFAULT_LANGUAGE;
  localStorage.setItem(STORAGE_KEY, language);

  return language;
};

export const getBrowserlanguage = () => {
  return navigator.language?.substring(0, 5);
};

export const getCountryCode = () => COUNTRIES[getLanguage()];

export const extractDistinctCountries = (courses) => {
  const map = new Map();
  return courses
    .map((c) => ({
      id: c.cId,
      name: c.country,
      abbr: c.abbr || c.countryAbbr || ''
    }))
    .filter((c) => {
      if (map.has(c.id)) return false;
      map.set(c.id, true);
      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const extractDistinctDistricts = (courses) => {
  const map = new Map();
  return courses
    .map((c) => ({
      id: c.dId,
      name: c.district,
      cId: c.cId,
      countryAbbr: c.abbr || c.countryAbbr || ''
    }))
    .filter((c) => {
      if (map.has(c.id)) return false;
      map.set(c.id, true);
      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const isQuotaExceeded = (e) => {
  let quotaExceeded = false;
  if (e) {
    if (e.code) {
      // eslint-disable-next-line default-case
      switch (e.code) {
        case 22:
          quotaExceeded = true;
          break;
        case 1014:
          // Firefox
          if (e.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
            quotaExceeded = true;
          }
          break;
      }
    } else if (e.number === -2147024882) {
      // Internet Explorer 8
      quotaExceeded = true;
    }
  }
  return quotaExceeded;
};
